import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, BadgeCheck } from "lucide-react";
import api from "@/lib/api";
import { useSEO } from "@/lib/seo";
import { trackEvent } from "@/lib/analytics";
import { useClientReady } from "@/hooks/useClientReady";

export default function Institutes() {
  const embedded = window.INITIAL_DATA?.type === "institutes" ? window.INITIAL_DATA.payload : null;
  const [institutes, setInstitutes] = useState(embedded?.institutes || []);
  const [loading, setLoading] = useState(!embedded);
  const clientReady = useClientReady();

  useSEO({
    title: "Partner Institutes & Universities — Training Basket",
    description: "Globally-ranked universities and India's leading IT training institute — explore programs by institution.",
    canonical: `${window.location.origin}/institutes`,
  });

  useEffect(() => {
    if (!clientReady) return;
    trackEvent("page_view", { page: "institutes" });
    api.get("/institutes").then((r) => setInstitutes(r.data.institutes)).catch(() => {}).finally(() => setLoading(false));
  }, [clientReady]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-14" data-testid="institutes-page">
      <p className="text-xs font-semibold uppercase tracking-widest text-rose-600">Partner network</p>
      <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 mt-2">Institutes & universities</h1>
      <p className="text-slate-500 mt-3 max-w-2xl">Every partner is vetted for accreditation, outcomes and learner support.</p>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">{[1, 2, 3].map((i) => <div key={i} className="h-52 rounded-2xl bg-slate-100 animate-pulse" />)}</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
          {institutes.map((i) => (
            <Link key={i.slug} to={`/institutes/${i.slug}`} data-testid={`institute-card-${i.slug}`}
              className="rounded-2xl border border-slate-200 bg-white p-7 hover:border-rose-300 hover:shadow-xl transition-all group">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-slate-900 text-white flex items-center justify-center font-extrabold shrink-0">{i.short}</div>
                <div>
                  <p className="font-bold text-slate-900 leading-snug group-hover:text-rose-700 transition-colors">{i.name}</p>
                  <p className="text-xs text-slate-400 flex items-center gap-1 mt-1"><MapPin className="w-3 h-3" />{i.city}{i.city && i.country ? ", " : ""}{i.country}</p>
                </div>
              </div>
              {i.ranking && <p className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-1.5 mt-4 inline-block">{i.ranking}</p>}
              {i.accreditation && <p className="text-xs text-slate-500 flex items-center gap-1.5 mt-3"><BadgeCheck className="w-3.5 h-3.5 text-emerald-500" />{i.accreditation}</p>}
              <p className="text-sm font-semibold text-rose-600 mt-4">{i.program_count} program{i.program_count === 1 ? "" : "s"} →</p>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
